import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, Lock, Paperclip, ShieldCheck } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import ServiceBrand from '../../components/public/ServiceBrand';
import { formatBytes } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { downloadSecretAttachment, resolveSecretMeta, revealSecret } from './secretsApi';

export default function PublicMessagePage() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(null);

  const meta = useQuery({
    queryKey: ['secret-meta', token],
    queryFn: () => resolveSecretMeta(token),
    retry: false,
  });

  const revealMutation = useMutation({
    mutationFn: () => revealSecret(token, password),
    onSuccess: (data) => setRevealed(data),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not open the message')),
  });

  // Auto-reveal unprotected messages once metadata loads.
  const { isSuccess: metaReady, data: metaData } = meta;
  const { mutate: open, isIdle } = revealMutation;
  useEffect(() => {
    if (metaReady && metaData && !metaData.requiresPassword && !revealed && isIdle) {
      open();
    }
  }, [metaReady, metaData, revealed, isIdle, open]);

  const handleDownload = async (attachment) => {
    const id = toast.loading(`Downloading ${attachment.name}…`);
    try {
      await downloadSecretAttachment(token, attachment);
      toast.success('Download ready', { id });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id });
    }
  };

  return (
    <div className="flex min-h-full items-start justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex justify-center"><ServiceBrand module="secure-drop" size="lg" /></div>

        <div className="rounded-2xl bg-slate-100 p-6 shadow-lg ring-1 ring-slate-200">
          {meta.isLoading ? (
            <Spinner />
          ) : meta.isError ? (
            <p className="py-8 text-center text-sm text-red-600">
              {getErrorMessage(meta.error, 'This message is unavailable or has self-destructed.')}
            </p>
          ) : meta.data.requiresPassword && !revealed ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <Lock className="h-8 w-8 text-slate-400" />
                <h1 className="text-base font-semibold text-slate-800">Password required</h1>
                <p className="text-sm text-slate-500">
                  This encrypted message is protected. Opening it may consume a view.
                </p>
              </div>
              <Input
                type="password"
                label="Password"
                value={password}
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && password && revealMutation.mutate()}
              />
              <Button
                className="w-full"
                loading={revealMutation.isPending}
                disabled={!password}
                onClick={() => revealMutation.mutate()}
              >
                Unlock &amp; open
              </Button>
            </div>
          ) : revealed ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-700">
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                <h1 className="font-semibold">Decrypted message</h1>
              </div>
              <pre className="whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
                {revealed.value}
              </pre>
              {revealed.attachments?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase text-slate-500">Attachments</p>
                  <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                    {revealed.attachments.map((a) => (
                      <li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                        <Paperclip className="h-4 w-4 text-slate-400" />
                        <span className="flex-1 truncate text-slate-700">{a.name}</span>
                        <span className="text-xs text-slate-400">{formatBytes(a.sizeBytes)}</span>
                        <button
                          onClick={() => handleDownload(a)}
                          className="rounded p-1 text-slate-400 hover:text-brand-600"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-center text-xs text-slate-400">
                This message may self-destruct after viewing — save anything you need now.
              </p>
            </div>
          ) : (
            <Spinner />
          )}
        </div>
      </div>
    </div>
  );
}
